/**
 * The evidence basis behind the Stage 2 proposals, as a comparable key.
 *
 * An accepted decision shows "Evidence changed" when the key it was accepted
 * under differs from today's, so the key must move exactly when evidence
 * Stage 2 reads moves:
 * - the content of the SED sections that the stance rules, the facts and the
 *   grid/gas availability resolver read. Not the SED's id or version: those
 *   change whenever any section is rebuilt, and a Public Sentiment refresh
 *   mints a new SED while every section Stage 2 reads stays the same.
 * - the latest Feasibility and Environmental reports. No Stage 2 rule reads
 *   Public Sentiment.
 * - the study-area acreage behind the land rules.
 *
 * User-authored inputs (the project intent, the promoted Generation & Impact
 * scenario) are excluded: acting on a proposal must not by itself mark the
 * proposal as resting on changed evidence.
 *
 * Pure. The caller supplies the section hasher, because the SED content-
 * identity module owns the canonical hashing scheme (volatile provenance
 * stripped).
 */
import type { SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';

/**
 * SED sections read by the stance rules, the facts and
 * `resolvePerSourceAvailability`. A test scans those sources, so a rule that
 * starts reading another section fails until the section is added here.
 */
export const STAGE2_EVIDENCE_SECTIONS = [
  // The Ozone Transport Region test places a site with no EPA listing by its
  // EIA electricity state (and reads that section's document provenance);
  // the regulatory state can only veto it, unless the section marks it as a
  // bounding-box estimate (DAT-FOLLOWUP-4).
  'electricity',
  // The air rule reads the EPA Green Book designation persisted here
  // (`environmental.data.naaqsDesignations`). The whole section is hashed, so
  // any habitat/wetland refresh also moves the key: a deliberate
  // over-approximation that never misses a designation change.
  'environmental',
  'fuelSupply',
  'grid',
  'nuclearInfra',
  'onSiteInfrastructure',
  'regulatory',
  'siteResearch',
  'solar',
  'transmissionHeadroom',
  'waterStress',
  'wind',
] as const;

/**
 * Content hash per requested SED section id. Every requested id maps to a
 * value; an absent section may map to null or to a stable "absent" hash.
 */
export type SedSectionHasher = (
  sedDocument: unknown,
  sectionIds: readonly string[],
) => Readonly<Record<string, string | null>>;

/** Prefix of every content-based key. Keys without it are legacy keys. */
const KEY_SCHEME = 'stage2-basis:v1';
/** Hash characters kept per section: enough to tell versions apart, short enough to store. */
const SECTION_HASH_CHARS = 16;

export interface Stage2BasisInputs {
  sed: unknown;
  feasibility: SiteRagReport | null;
  environmental: SiteRagReport | null;
  studyAreaAcres: number | null;
}

/**
 * The content-based key, readable per part so a stored key shows which input
 * moved: `stage2-basis:v1|fuelSupply=…;grid=…;…|reports=feasibility:id,…|acres=500`.
 */
export function stage2BasisKey(inputs: Stage2BasisInputs, hashSedSections: SedSectionHasher): string {
  let sedPart = 'sed=none';
  if (inputs.sed) {
    const hashes = hashSedSections(inputs.sed, STAGE2_EVIDENCE_SECTIONS);
    sedPart = STAGE2_EVIDENCE_SECTIONS
      .map(id => `${id}=${hashes[id]?.slice(0, SECTION_HASH_CHARS) ?? 'absent'}`)
      .join(';');
  }
  const reportPart = [inputs.feasibility, inputs.environmental]
    .filter((report): report is SiteRagReport => report != null)
    .map(report => `${report.reportType ?? 'feasibility'}:${report.id}`)
    .join(',');
  const acres = inputs.studyAreaAcres != null && Number.isFinite(inputs.studyAreaAcres)
    ? String(Math.round(inputs.studyAreaAcres))
    : 'unknown';
  return [KEY_SCHEME, sedPart, `reports=${reportPart || 'none'}`, `acres=${acres}`].join('|');
}

/**
 * The key format saved before content-based keys: SED id@version plus the
 * latest Stage 1 report ids (Public Sentiment included). Frozen, because
 * acceptances already stored in this format are compared against it.
 */
export function stage2LegacyBasisKey(
  sed: SiteEvidenceDocument | null | undefined,
  stage1: readonly (SiteRagReport | null)[],
): string {
  const sedPart = sed ? `${sed.id}@${sed.version}` : 'no-sed';
  const reportPart = stage1
    .filter((report): report is SiteRagReport => report != null)
    .map(report => `${report.reportType ?? 'feasibility'}:${report.id}`)
    .join(',');
  return `${sedPart}|${reportPart || 'no-reports'}`;
}

/** Today's basis in both formats, so a saved key compares like for like. */
export interface Stage2CurrentBasis {
  /** The key new acceptances store: content-based once a hasher is wired, legacy until then. */
  key: string;
  legacyKey: string;
}

/**
 * Whether a decision accepted under `acceptedKey` now rests on different
 * evidence. A content-based key compares with today's content-based key. A
 * legacy key compares with today's legacy key, exactly as the old check did:
 * it cannot tell which section moved, so any SED rebuild raises the flag.
 * Over-flagging is the safe direction; re-accepting stores a content-based
 * key.
 */
export function stage2BasisChanged(acceptedKey: string, current: Stage2CurrentBasis): boolean {
  return acceptedKey.startsWith(`${KEY_SCHEME}|`)
    ? acceptedKey !== current.key
    : acceptedKey !== current.legacyKey;
}
